/**
 * Product detail page: the hero gallery slider and the stock-list tabs.
 *
 * Both bind on DOMContentLoaded (app.js calls initProductGallery() and
 * initProductStockTabs() there) and bail cleanly when their markup is
 * absent, the same contract carousel.js and gallery.js already follow.
 *
 * HERO GALLERY. template-parts/product/hero.php already renders a working,
 * zero-JS gallery: a horizontally scroll-snapped track
 * (data-awdd-product-gallery-track) that is fully swipeable with pure CSS.
 * This module only layers prev/next buttons on top -- it must never be
 * REQUIRED to make the gallery usable, per that template's own docblock.
 * Because the track ships with no button markup of its own (unlike
 * card-carousel, whose block template pre-renders its prev/next elements
 * for carousel.js to find), the buttons are created here rather than bound
 * to existing markup. They are appended as siblings of the track, not
 * children, so they sit on top of it without disturbing the scroll-snap
 * children carousel.js's own currentIndex()/scrollToSlide() approach is
 * reused unchanged for.
 *
 * NEITHER MODULE CREATES ANYTHING CARRYING .reveal. app.js only wires up
 * .reveal once, at DOMContentLoaded, against whatever is already in the
 * DOM at that moment -- a .reveal element created afterwards (the gallery
 * buttons here) or one that starts off-screen via `display:none` (the CDJ
 * stock panel, see stock-tabs.php) never gets a working GSAP tween and
 * stays permanently invisible. This is the same trap project-archive's
 * JS-built tiles document and avoid.
 *
 * STOCK TABS. The two panels stock-tabs.php renders are both real DOM
 * nodes: the second already carries the literal Tailwind `hidden` class in
 * markup, so with this module dead the first table is still visible and
 * the second is simply unreachable (never a click that silently does
 * nothing). Switching tabs only ever toggles that one class -- never an
 * inline `style` this module would have to undo later.
 */
import { icon } from './icons.js';

const GOLD_ARROW_BUTTON =
	'flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-ink-deep/40 text-white backdrop-blur-sm transition-colors hover:border-gold hover:text-gold disabled:pointer-events-none disabled:opacity-0';

const ARROW_SVG = icon( 'arrow', 'h-4 w-4' );

export function initProductGallery() {
	document.querySelectorAll('[data-awdd-product-gallery-track]').forEach((track) => {
		const slides = Array.from(track.children);
		if (slides.length < 2) return;

		const host = track.parentElement;
		if (!host) return;

		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		const currentIndex = () => {
			const x = track.scrollLeft + track.offsetLeft;
			let best = 0;
			let bestDist = Infinity;
			slides.forEach((s, i) => {
				const d = Math.abs(s.offsetLeft - x);
				if (d < bestDist) { bestDist = d; best = i; }
			});
			return best;
		};

		const scrollToSlide = (i) => {
			const target = slides[Math.max(0, Math.min(i, slides.length - 1))];
			track.scrollTo({
				left: target.offsetLeft - track.offsetLeft,
				behavior: reduceMotion ? 'auto' : 'smooth',
			});
		};

		const makeButton = (side, label) => {
			const b = document.createElement('button');
			b.type = 'button';
			b.setAttribute('aria-label', label);
			b.className = `absolute top-1/2 -translate-y-1/2 z-10 ${side === 'prev' ? 'left-3' : 'right-3'} ${GOLD_ARROW_BUTTON}`;
			b.innerHTML = `<span class="${side === 'prev' ? 'rotate-180' : ''}">${ARROW_SVG}</span>`;
			return b;
		};

		const prev = makeButton('prev', 'Previous image');
		const next = makeButton('next', 'Next image');
		prev.addEventListener('click', () => scrollToSlide(currentIndex() - 1));
		next.addEventListener('click', () => scrollToSlide(currentIndex() + 1));

		host.appendChild(prev);
		host.appendChild(next);

		const sync = () => {
			const i = currentIndex();
			prev.disabled = i <= 0;
			next.disabled = i >= slides.length - 1;
		};

		let raf = 0;
		track.addEventListener('scroll', () => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(sync);
		}, { passive: true });

		window.addEventListener('resize', () => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(sync);
		}, { passive: true });

		sync();
	});
}

const TAB_ON = ['-mb-px', 'border-b-2', 'border-steel-500', 'text-ink'];
const TAB_OFF = ['text-mute2', 'hover:text-ink'];

export function initProductStockTabs() {
	document.querySelectorAll('[data-awdd-product-stock-tabs]').forEach((root) => {
		const tabs = Array.from(root.querySelectorAll('[data-awdd-product-stock-tab]'));
		const panels = Array.from(root.querySelectorAll('[data-awdd-product-stock-panel]'));
		if (!tabs.length || !panels.length) return;

		const activate = (key) => {
			tabs.forEach((tab) => {
				const on = tab.getAttribute('data-awdd-product-stock-tab') === key;
				tab.setAttribute('aria-selected', String(on));
				TAB_ON.forEach((c) => tab.classList.toggle(c, on));
				TAB_OFF.forEach((c) => tab.classList.toggle(c, !on));
			});
			panels.forEach((panel) => {
				panel.classList.toggle('hidden', panel.getAttribute('data-awdd-product-stock-panel') !== key);
			});
		};

		tabs.forEach((tab) => {
			tab.addEventListener('click', () => activate(tab.getAttribute('data-awdd-product-stock-tab')));
		});
	});
}

export default function initProduct() {
	initProductGallery();
	initProductStockTabs();
}
